import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Edit2, Trash2, X, Save, List, LayoutGrid, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import holidayService, { Holiday, HolidayCreateDto, HolidayUpdateDto } from '../services/holidayService';

const Holidays = () => {
  const { t } = useTranslation();

  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formData, setFormData] = useState<HolidayCreateDto>({
    name: '',
    date: new Date().toISOString().split('T')[0],
    endDate: '',
    holidayType: 'NATIONAL',
    description: '',
    isPaid: true,
  });

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState<Holiday | null>(null);

  useEffect(() => {
    loadHolidays();
  }, [selectedYear]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const response = await holidayService.getAll(selectedYear);
      if (response.success && response.data) {
        setHolidays(response.data);
      } else {
        setError(response.message || t('holidays.loadError'));
      }
    } catch (err) {
      setError(t('holidays.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredHolidays = holidays
    .filter(h => h.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 h.holidayName.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const isUpcoming = (date: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(date) >= today;
  };

  const isPast = (date: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(date) < today;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getHolidayTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'NATIONAL': t('holidays.national'),
      'RELIGIOUS': t('holidays.religious'),
      'COMPANY': t('holidays.company'),
    };
    return types[type] || type;
  };

  const getHolidayTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'NATIONAL': 'bg-red-50 text-red-600',
      'RELIGIOUS': 'bg-amber-50 text-amber-600',
      'COMPANY': 'bg-blue-50 text-blue-600',
    };
    return colors[type] || 'bg-slate-50 text-slate-600';
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    setFormData({
      name: '',
      date: new Date().toISOString().split('T')[0],
      endDate: '',
      holidayType: 'NATIONAL',
      description: '',
      isPaid: true,
    });
    setShowModal(true);
  };

  const openEditModal = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name || holiday.holidayName,
      date: holiday.date?.split('T')[0] || holiday.holidayDate?.split('T')[0],
      endDate: holiday.endDate?.split('T')[0] || '',
      holidayType: holiday.holidayType || 'NATIONAL',
      description: holiday.description || '',
      isPaid: holiday.isPaid,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (editingHoliday) {
        const updateData: HolidayUpdateDto = {
          name: formData.name,
          date: formData.date,
          endDate: formData.endDate || undefined,
          holidayType: formData.holidayType,
          description: formData.description,
          isPaid: formData.isPaid,
        };
        const response = await holidayService.update(editingHoliday.id, updateData);
        if (response.success) {
          setShowModal(false);
          loadHolidays();
        } else {
          setError(response.message || t('holidays.updateError'));
        }
      } else {
        const createData: HolidayCreateDto = {
          name: formData.name,
          date: formData.date,
          endDate: formData.endDate || undefined,
          holidayType: formData.holidayType,
          description: formData.description || undefined,
          isPaid: formData.isPaid,
        };
        const response = await holidayService.create(createData);
        if (response.success) {
          setShowModal(false);
          loadHolidays();
        } else {
          setError(response.message || t('holidays.createError'));
        }
      }
    } catch (err: any) {
      console.error('Holiday submit error:', err);
      setError(err?.message || t('holidays.operationError'));
    }
  };

  const confirmDelete = (holiday: Holiday) => {
    setDeletingHoliday(holiday);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingHoliday) return;
    try {
      const response = await holidayService.delete(deletingHoliday.id);
      if (response.success) {
        setShowDeleteConfirm(false);
        setDeletingHoliday(null);
        loadHolidays();
      } else {
        setError(response.message || t('holidays.deleteError'));
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      setError(t('holidays.deleteOperationError'));
      setShowDeleteConfirm(false);
    }
  };

  const upcomingCount = holidays.filter(h => isUpcoming(h.date || h.holidayDate)).length;
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('holidays.title')}</h1>
          <p className="text-slate-500 text-sm">{t('holidays.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded ${viewMode === 'card' ? 'bg-white shadow-sm' : ''}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <List size={18} />
            </button>
          </div>
          <button
            onClick={openAddModal}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={18} /> {t('holidays.addHoliday')}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-600" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X size={18} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Statistics */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Info size={18} className="text-blue-600" /> {t('holidays.statistics')}
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">{t('holidays.upcomingHolidays')}</span>
                <span className="text-lg font-bold text-green-600">{upcomingCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">{t('holidays.totalForYear', { year: selectedYear })}</span>
                <span className="text-lg font-bold text-slate-800">{holidays.length}</span>
              </div>
              <div className="pt-4 border-t border-slate-100">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-700 font-medium leading-relaxed">
                    {t('holidays.holidayPayrollInfo')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-5 text-white shadow-lg">
            <h4 className="font-bold mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="text-green-400" /> {t('holidays.policyInfo')}
            </h4>
            <p className="text-xs text-slate-200 leading-relaxed mb-4">
              {t('holidays.shiftPolicyInfo')}
            </p>
          </div>
        </div>

        {/* Holiday List */}
        <div className="lg:col-span-3 space-y-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={t('holidays.searchHoliday')}
              className="w-full pl-4 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Card View */}
          {viewMode === 'card' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredHolidays.map(holiday => {
                const dateStr = holiday.date || holiday.holidayDate;
                const upcoming = isUpcoming(dateStr);
                const past = isPast(dateStr);
                return (
                  <div
                    key={holiday.id}
                    className={`bg-white rounded-xl border p-4 shadow-sm transition-all hover:shadow-md ${
                      upcoming ? 'border-green-200 ring-1 ring-green-50' : past ? 'border-slate-200 opacity-60' : 'border-slate-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2.5 rounded-lg ${getHolidayTypeColor(holiday.holidayType || '')}`}>
                          <Calendar size={20} />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800">{holiday.name || holiday.holidayName}</h4>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getHolidayTypeColor(holiday.holidayType || '')}`}>
                            {getHolidayTypeLabel(holiday.holidayType || '')}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(holiday)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => confirmDelete(holiday)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                      <Calendar size={14} className="text-slate-400" />
                      <span className="font-medium">{formatDate(dateStr)}</span>
                    </div>

                    {holiday.endDate && holiday.endDate !== dateStr && (
                      <div className="text-xs text-slate-400 mb-2">
                        {t('holidays.end')}: {formatShortDate(holiday.endDate)}
                      </div>
                    )}

                    {holiday.description && (
                      <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                        {holiday.description}
                      </p>
                    )}

                    <div className="mt-3 pt-3 border-t border-slate-50 flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        holiday.isPaid ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {holiday.isPaid ? t('holidays.paid') : t('holidays.unpaid')}
                      </span>
                      {upcoming && (
                        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          {t('holidays.upcoming')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {filteredHolidays.length === 0 && (
                <div className="col-span-2 text-center py-12 text-slate-400">
                  <Calendar size={48} className="mx-auto mb-2 opacity-30" />
                  <p>{t('holidays.noHolidays')}</p>
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('holidays.name')}</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('holidays.date')}</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('holidays.type')}</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('holidays.payStatus')}</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('leaves.status')}</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('leaves.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredHolidays.map(holiday => {
                    const dateStr = holiday.date || holiday.holidayDate;
                    const upcoming = isUpcoming(dateStr);
                    return (
                      <tr key={holiday.id} className={`hover:bg-slate-50 ${!upcoming ? 'opacity-60' : ''}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${getHolidayTypeColor(holiday.holidayType || '')}`}>
                              <Calendar size={18} />
                            </div>
                            <span className="font-medium text-slate-800">{holiday.name || holiday.holidayName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600">{formatShortDate(dateStr)}</td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${getHolidayTypeColor(holiday.holidayType || '')}`}>
                            {getHolidayTypeLabel(holiday.holidayType || '')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            holiday.isPaid ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {holiday.isPaid ? t('holidays.paid') : t('holidays.unpaid')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {upcoming ? (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-50 text-green-600">
                              {t('holidays.upcoming')}
                            </span>
                          ) : (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-500">
                              {t('holidays.past')}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(holiday)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => confirmDelete(holiday)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredHolidays.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Calendar size={48} className="mx-auto mb-2 opacity-30" />
                  <p>{t('holidays.noHolidays')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">
                {editingHoliday ? t('holidays.editHoliday') : t('holidays.addHoliday')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('holidays.name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('holidays.namePlaceholder')}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('holidays.startDate')} *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('holidays.endDate')}</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('holidays.holidayType')}</label>
                  <select
                    value={formData.holidayType}
                    onChange={(e) => setFormData({ ...formData, holidayType: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="NATIONAL">{t('holidays.national')}</option>
                    <option value="RELIGIOUS">{t('holidays.religious')}</option>
                    <option value="COMPANY">{t('holidays.company')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('holidays.payStatus')}</label>
                  <select
                    value={formData.isPaid ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, isPaid: e.target.value === 'true' })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">{t('holidays.paid')}</option>
                    <option value="false">{t('holidays.unpaid')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('holidays.descriptionLabel')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder={t('holidays.descriptionPlaceholder')}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Save size={18} /> {editingHoliday ? t('holidays.update') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingHoliday && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('holidays.deleteHoliday')}</h3>
              <p className="text-slate-500 mb-6" dangerouslySetInnerHTML={{
                __html: t('holidays.deleteConfirm', { name: deletingHoliday.name || deletingHoliday.holidayName })
              }} />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Holidays;
